import { defineStore, storeToRefs } from "pinia";
import { socket } from '@/user.socket';
import { socket as feedsocket } from "@/feed.socket";
import { useResponseMessageStore } from "./response_message";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "./auth";
import { useFixturesStore } from "./fixtures";

// TKT-10b: an accept/refuse offer emits over the socket and must not hang if the
// server never answers. The reply timeout and the `t`/message-store references
// (captured in bindSocketEvents, a setup context — useI18n can't be called from
// an action) live at module scope; the store is a client-only singleton.
let replyTimeout = null;
let pendingRefuse = null;
let i18nT = null;
let responseMessages = null;
const REPLY_TIMEOUT_MS = 15000;

export const useBetslipStore = defineStore('betslip', {
    state: () => ({
        slips: {},
        oddType: 'p',
        changes: false,
        updates: false,
        confirming: false,
        ticketId: null,
        sendingTicket: false,
    }),

    getters: {
        getSendingTicket: state => state.sendingTicket,
        hasChanges: state => state.changes,
        hasUpdates: state => state.updates,
        getOddType: state => state.oddType,
        getSlips: state => state.slips,
        isHighlited: state => uid => {
            for (let eid in state.slips) {
                if (uid in state.slips[eid].betslips) {
                    return true
                }
            }
            return false
        },
        isEventSelected: state => eid => eid in state.slips,
        cols(state) {
            let counter = 1
            for (let eid in state.slips) {
                counter *= Object.keys(state.slips[eid].betslips).length
            }
            return counter
        },
        selections(state) {
            let counter = 0
            for (let eid in state.slips) {
                counter += Object.keys(state.slips[eid].betslips).length
            }

            return counter
        },
        totalOdd(state) {
            let odd = 1;
            for (let eid in state.slips) {
                for (let uid in state.slips[eid].betslips) {
                    odd *= state.slips[eid].betslips[uid].p
                }
            }
            return odd
        },

        minAndMax: state => {
            let mins = []
            let maxs = []

            for (let eid in state.slips) {
                let min = 9999999
                let max = 1
                for (let uid in state.slips[eid].betslips) {
                    if (state.slips[eid].betslips[uid].p < min) min = state.slips[eid].betslips[uid].p
                    if (state.slips[eid].betslips[uid].p > max) max = state.slips[eid].betslips[uid].p
                }

                maxs.push(max)
                mins.push(min)
            }

            return [
                mins.reduce((acc, number) => acc * number, 1),
                maxs.reduce((acc, number) => acc * number, 1)
            ]
        },

        bonusValue(state){
           const auth = useAuthStore()
            const { getUser } = storeToRefs(auth)
            let bonusPerc
            if(this.selections && getUser.value?.tb ){
                if(getUser.value?.tb[this.selections] ){
                    bonusPerc = getUser.value?.tb[this.selections] 
                }
            }
            return bonusPerc
        }
    },

    actions: {
        setOddType (type) {
            this.oddType = type
            localStorage.setItem('oddType', type)
        },

        initializeFromLocalStorage() {
            let t = localStorage.getItem('ticket')
            const ticketIdAtReload = localStorage.getItem('ticketIdAtReload')
            if (t) {
                this.slips = JSON.parse(t)
            }

            if (ticketIdAtReload) {
                this.ticketId = ticketIdAtReload
            }
        },

        setOdd(odd, eid, mi , mnrs) {
            if (odd.st === 2 || odd.st === 3) return;

            if (!(eid in this.slips)) {
                this.slips[eid] = { betslips: {} }  
            }

            if (!(odd.i in this.slips[eid].betslips)) {
                const copy = JSON.parse(JSON.stringify(odd))
                copy.mi = mi  
                copy.mnrs = mnrs 
                this.slips[eid].betslips[odd.i] = copy
            } else {
                delete this.slips[eid].betslips[odd.i]

                if (!Object.keys(this.slips[eid].betslips).length) {
                    delete this.slips[eid]
                }
            }

            localStorage.setItem('ticket', JSON.stringify(this.slips))
        },
        updateOdd(eid, odd) {
            if (this.confirming) return;
            if (this.updateKoef(eid, odd)) {
                this.updates = true
            }
            localStorage.setItem('ticket', JSON.stringify(this.slips))
        },

        updateKoef(eid, odd) {
            if (!this.changes) {
                if (eid in this.slips) {
                    if (odd.i in this.slips[eid].betslips) {
                        this.slips[eid].betslips[odd.i] = odd
                        return true
                    }
                }
            }
            return false
        },

        acceptUpdates() {
            this.updates = false
          
        },

        pullSlip(eid) {
            if (eid in this.slips) {
                delete this.slips[eid]
            }

            localStorage.setItem('ticket', JSON.stringify(this.slips))
        },

        send(cur_id, amount, childId = null) {
            if (this.changes) {
                this.acceptChanges()
                return;
            }

            try {
                this.sendingTicket = true
                const fixtures = useFixturesStore()
                const { getEvent } = storeToRefs(fixtures)

                for (let event in this.slips) {
                    let e;
                    e = JSON.parse(JSON.stringify(getEvent.value(event)))
                    this.slips[e.i] = {...this.slips[e.i], ...e}
                }
                const ticketData = {
                    cur_id,
                    amount: Number(amount),
                    ticket: this.slips,
                }
        
                if (childId) {
                    ticketData.childId = childId
                }
                socket.emit('ticket:create', ticketData)
            } catch (error) {
                this.sendingTicket = false
                console.error(error.message)
            }
        },

        clear() {
            this.sendingTicket = false
            this.slips = {}
            this.confirming = false
            localStorage.setItem('ticket', '')
        },

        acceptChanges() {
            if (!this.ticketId) return;

            socket.emit('ticket:accept-offer', {ticketId: this.ticketId})
            // TKT-10b: do NOT clear here. Clearing before the server answered made
            // a rejection (be/users emits {ok:false} on this leg) look identical to
            // a success — slip gone, no error, and the ticketId needed to recover
            // already discarded. Keep everything; the ok handler clears, the else
            // shows the error, and a timeout surfaces a no-reply.
            this.sendingTicket = true
            this.armReplyTimeout()
        },

        rejectChanges() {
            if (!this.ticketId) return;

            // TKT-10b: a refusal's happy path is the offer simply going away, so
            // clear the offer state optimistically for immediate feedback — but
            // STASH it first so a failed refusal (the leg used to have no handler)
            // can restore it, instead of losing it silently with the ticketId
            // already discarded up front as before. The slips themselves are kept
            // (as they always were), so the player can re-submit.
            pendingRefuse = { ticketId: this.ticketId, changes: this.changes }
            socket.emit('ticket:refuse-offer', {ticketId: this.ticketId})
            this.changes = false
            this.ticketId = null
            localStorage.removeItem('ticketIdAtReload')
        },

        // TKT-10b: an offer-leg rejection message — name the blocked odd from the
        // local fixtures store (same as ticket:create), else the backend message,
        // else the generic key TKT-10a falls back to.
        offerErrorMessage(data) {
            let msg = ''
            if (data.blocked && data.blocked.eid) {
                const ev = useFixturesStore().getEvent(data.blocked.eid)
                let event = ''
                if (ev) {
                    const isMatch = ev.o == 'false' || ev.o === false
                    event = isMatch
                        ? (ev.h && ev.a ? `${ev.h} v ${ev.a}` : '')
                        : (ev.outrightName || '')
                }
                if (event) {
                    msg = i18nT('betslips.oddBlocked', { event, odd: data.blocked.oddnm || '' })
                }
            }
            return msg || i18nT(data.message || 'betslips.generic', { extra: data.extra })
        },

        armReplyTimeout() {
            this.clearReplyTimeout()
            replyTimeout = setTimeout(() => {
                replyTimeout = null
                // No accept-offer reply arrived — surface it and stop the spinner
                // rather than hang; the slip stays so nothing is silently lost.
                this.sendingTicket = false
                if (responseMessages && i18nT) responseMessages.setMessage(i18nT('betslips.generic'))
            }, REPLY_TIMEOUT_MS)
        },

        clearReplyTimeout() {
            if (replyTimeout) { clearTimeout(replyTimeout); replyTimeout = null }
        },

        bindSocketEvents() {
            const responseMessageStore = useResponseMessageStore()
            const { t } = useI18n()
            // TKT-10b: capture for the actions/timeout, which run outside a setup
            // context and so cannot call useI18n themselves.
            i18nT = t
            responseMessages = responseMessageStore

            socket.on('ticket:create', (data) => {
                if (data.ok) {
                    responseMessageStore.setMessage(t('betslips.created'))
                    const pathParts = window.location.pathname.split('/');
                    const eidFromUrl = pathParts.includes('in-play') ? pathParts[pathParts.indexOf('in-play') + 1] : null;
                    for (let event in this.slips) {
                        if (event !== eidFromUrl) {
                            feedsocket.emit('leave-fixture-odds', {i: event})  
                        }
                    }
                    this.clear()
                } else {
                    this.sendingTicket = false
                    this.confirming = false
                    // A selection blocked during creation carries
                    // `blocked: { eid, oddid, oddnm, mfid }`; offerErrorMessage names
                    // it (TKT-9b), else falls back to the backend/generic message.
                    responseMessageStore.setMessage(this.offerErrorMessage(data))
                }
            })
            socket.on('ticket:confirming', (data) => {
                if (data.ok) {
                    this.confirming = true
                    responseMessageStore.setMessage(t(data.message, {extra: data.extra}))
                }
            })

            socket.on('ticket:offer', (data) => { 
                if (data.ok) {
                    responseMessageStore.setMessage(t(data.message, {extra: data.extra}))

                    for (let eid in data.data.s) {
                        for (let uid in data.data.s[eid]) {
                            if (this.slips[eid].betslips[uid].p > data.data.s[eid][uid]) {
                                this.slips[eid].betslips[uid].n = 1
                            } else {
                                this.slips[eid].betslips[uid].n = 2
                            }

                            this.slips[eid].betslips[uid].p = data.data.s[eid][uid]

                            this.changes = true
                            this.sendingTicket = false
                        }
                    }
                    this.ticketId = data.data._id
                    localStorage.setItem('ticket', JSON.stringify(this.slips))
                    localStorage.setItem('ticketIdAtReload', this.ticketId)
                } else {
                    // TKT-10b: a failed offer used to be silent (no else).
                    this.sendingTicket = false
                    responseMessageStore.setMessage(this.offerErrorMessage(data))
                }
            })

            socket.on('ticket:accept-offer', (data) => {
                this.clearReplyTimeout()
                this.sendingTicket = false
                if (data.ok) {
                    // Confirmed — NOW it is safe to clear the slip (TKT-10b: this
                    // used to happen synchronously in acceptChanges, before this).
                    responseMessageStore.setMessage(t('betslips.created'))
                    this.changes = false
                    this.ticketId = null
                    localStorage.removeItem('ticketIdAtReload')
                    this.clear()
                } else {
                    // TKT-10b: rejection — keep the slip and ticketId (recoverable)
                    // and show a translated error instead of a silent empty slip.
                    responseMessageStore.setMessage(this.offerErrorMessage(data))
                }
            })

            socket.on('ticket:refuse-offer', (data) => {
                // TKT-10b: no handler existed — a failed refusal was silent. The
                // offer was already cleared optimistically in rejectChanges; here
                // we only need to restore it if the server rejected the refusal.
                if (data.ok) {
                    pendingRefuse = null // confirmed; nothing to restore
                    if (data.message) responseMessageStore.setMessage(t(data.message, {extra: data.extra}))
                } else {
                    // Refusal failed: restore the offer so it stays actionable
                    // (previously thrown away and silent), and surface the error.
                    if (pendingRefuse) {
                        this.ticketId = pendingRefuse.ticketId
                        this.changes = pendingRefuse.changes
                        localStorage.setItem('ticketIdAtReload', pendingRefuse.ticketId)
                        pendingRefuse = null
                    }
                    responseMessageStore.setMessage(this.offerErrorMessage(data))
                }
            })
        }
    }
})